<div class="admin-main">
<div class="gsr-page">

  <div class="gsr-header">
    <button type="button" class="gsr-back" (click)="back()" aria-label="Back">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div class="gsr-heading">
      <h2 class="gsr-title">
        @if (moduleLabel) {
          <mat-icon class="gsr-title-icon">{{ moduleIcon }}</mat-icon>
          {{ moduleLabel }}
        } @else {
          Search
        }
      </h2>
      @if (query) {
        <p class="gsr-subtitle">Results for "{{ query }}"</p>
      }
    </div>
    @if (data) {
      <span class="gsr-total">{{ data.total }} {{ data.total === 1 ? 'result' : 'results' }}</span>
    }
  </div>

  @if (invalidModule) {
    <div class="gsr-state">
      <mat-icon>search_off</mat-icon>
      <p class="gsr-state-title">Unknown search category</p>
      <p class="gsr-state-text">"{{ moduleKey }}" is not a searchable category.</p>
    </div>
  } @else if (tooShort) {
    <div class="gsr-state">
      <mat-icon>search</mat-icon>
      <p class="gsr-state-title">Enter at least 2 characters</p>
      <p class="gsr-state-text">Type a search term in the header search box.</p>
    </div>
  } @else if (isLoading) {
    <div class="gsr-state">
      <mat-progress-spinner [diameter]="28" mode="indeterminate"></mat-progress-spinner>
      <p class="gsr-state-text">Searching…</p>
    </div>
  } @else if (errorMessage) {
    <div class="gsr-state gsr-error">
      <mat-icon>error_outline</mat-icon>
      <p class="gsr-state-title">Search is unavailable</p>
      <p class="gsr-state-text">{{ errorMessage }}</p>
      @if (requestedPage > 1) {
        <div class="gsr-pager-buttons gsr-error-actions">
          <button type="button" (click)="goToPage(1)">Back to first page</button>
        </div>
      }
    </div>
  } @else if (data && data.items.length === 0) {
    <div class="gsr-state">
      <mat-icon>search_off</mat-icon>
      <p class="gsr-state-title">No results found</p>
      <p class="gsr-state-text">Try different keywords or check your spelling.</p>
    </div>
  } @else if (data) {
    <div class="gsr-list">
      @for (item of data.items; track trackByItem($index, item)) {
        <div class="gsr-row" [class.no-route]="!item.route" (click)="open(item)"
          [attr.title]="item.route ? null : 'The record this document belongs to cannot be opened from search'">
          <mat-icon class="gsr-row-icon">{{ item.module === 'document' ? (item.isFolder ? 'folder' : 'insert_drive_file') : item.moduleIcon }}</mat-icon>
          <div class="gsr-row-text">
            @if (item.module === 'document') {
              <!-- Documents keep their original, unchanged row — see the
                   matching comment in global-search.component.html. -->
              <span class="gsr-row-title">{{ item.title }}</span>
              @if (item.subtitle) {
                <span class="gsr-row-subtitle">{{ item.subtitle }}</span>
              }
            } @else {
              <span class="gsr-row-line1">
                @if (item.subtitle) {
                  <span class="gsr-row-identifier">{{ item.subtitle }}</span>
                }
                <span class="gsr-row-name">{{ item.title }}</span>
              </span>
              @if (item.description) {
                <span class="gsr-row-description" [innerHTML]="highlight(item.description)"></span>
              }
            }
          </div>
          <!-- The record's real team — e.g. a Bid's Sales Rep + Estimator + Project
               Manager + Assigned To — absent (not a placeholder) when the record has
               no role filled in, or for a module with no team/assignee concept. Never
               permission-gated (not financial data) — see GlobalSearchApiHit.team/
               assignee_id/assignee_name. Positioned before the amount. A single member
               looks identical to the previous single-avatar + name-text treatment;
               more than one stacks the circles instead, matching the Bids/Jobs list
               "Team" column (app-avatar-group). -->
          @if (teamAvatars(item); as avatars) {
            @if (avatars.length) {
              <span class="gsr-row-assignee-group">
                @if (avatars.length === 1) {
                  <span class="gsr-row-assignee" [class.has-photo]="avatars[0].profileImageUrl" [style.background-color]="avatars[0].profileImageUrl ? null : avatars[0].color" [attr.title]="avatars[0].tooltip">
                    <!-- Same real uploaded photo app-avatar-group already shows for
                         this exact person elsewhere (Bid/Job detail header). -->
                    @if (avatars[0].profileImageUrl) {
                      <img [src]="avatars[0].profileImageUrl" alt="" class="gsr-row-assignee-photo" />
                    } @else {
                      {{ avatars[0].initials }}
                    }
                    <!-- Same small presence-status badge the real Bids/Jobs
                         list "Team" column avatars show (app-avatar-group). -->
                    <span class="gsr-row-status-badge" [innerHTML]="avatars[0].statusSvg"></span>
                  </span>
                  <span class="gsr-row-assignee-name">{{ avatars[0].name }}</span>
                } @else {
                  <span class="gsr-row-team-stack">
                    @for (member of avatars; track member.name + member.roles.join(',')) {
                      <span class="gsr-row-assignee gsr-row-team-avatar" [class.has-photo]="member.profileImageUrl" [style.background-color]="member.profileImageUrl ? null : member.color" [attr.title]="member.tooltip">
                        @if (member.profileImageUrl) {
                          <img [src]="member.profileImageUrl" alt="" class="gsr-row-assignee-photo" />
                        } @else {
                          {{ member.initials }}
                        }
                        <span class="gsr-row-status-badge" [innerHTML]="member.statusSvg"></span>
                      </span>
                    }
                  </span>
                }
              </span>
            }
          }
          <!-- Bid/Proposal/Job's genuine total value — absent (not a placeholder)
               when the backend redacted it for a user without that module's View
               Financial permission. See GlobalSearchApiHit.amount. -->
          @if (item.amount != null) {
            <span class="gsr-row-amount">{{ item.amount | currency }}</span>
          }
          @if (item.route) {
            <mat-icon class="gsr-row-arrow">arrow_forward</mat-icon>
          }
        </div>
      }
    </div>

    <div class="gsr-pager">
      <span class="gsr-pager-info">
        {{ data.from }}–{{ data.to }} of {{ data.total }}
      </span>
      <div class="gsr-pager-buttons">
        <button type="button" [disabled]="data.currentPage <= 1" (click)="goToPage(1)" aria-label="First page">
          <mat-icon>first_page</mat-icon>
        </button>
        <button type="button" [disabled]="data.currentPage <= 1" (click)="goToPage(data.currentPage - 1)">
          <mat-icon>chevron_left</mat-icon> Previous
        </button>
        <span class="gsr-pager-page">Page {{ data.currentPage }} of {{ data.lastPage }}</span>
        <button type="button" [disabled]="!data.hasMore" (click)="goToPage(data.currentPage + 1)">
          Next <mat-icon>chevron_right</mat-icon>
        </button>
        <button type="button" [disabled]="!data.hasMore" (click)="goToPage(data.lastPage)" aria-label="Last page">
          <mat-icon>last_page</mat-icon>
        </button>
      </div>
    </div>
  }
</div>
</div>
